import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import CustomSelect from "../../../components/ui/Select";
import CustomTextField from "../../../components/ui/Textfield";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { BookingContactEntry } from "../../../types/bookings";
import { IContactFormData } from "../../../types/contact";
import {
  CONTACT_ROLE_OPTIONS,
  DEFAULT_CONTACT_ROLE,
  PRIMARY_CONTACT_ROLE,
} from "../constants/contact-roles";
import { buildRowContact } from "../utils/booking-contacts";
import { Add } from "@mui/icons-material";

interface BookingContactsFieldProps {
  /** The subset of rows this editor manages. */
  rows: BookingContactEntry[];
  /** Receives the modified subset. */
  onChange: (next: BookingContactEntry[]) => void;
  contactOptions: IContactFormData[];
  contactLoading: boolean;
  setContactSearchTerm: (term: string) => void;
  /** Opens the caller's inline "create contact" dialog. */
  onNewContactClick?: () => void;
  title?: string;
  /** Makes the first added row the primary (individual-client booking types). */
  firstIsPrimary?: boolean;
  defaultRole?: string;
  /** "plain" = inline rows; "card" = bordered rows with a dashed empty state. */
  variant?: "plain" | "card";
}

const BookingContactsField: React.FC<BookingContactsFieldProps> = ({
  rows,
  onChange,
  contactOptions,
  contactLoading,
  setContactSearchTerm,
  onNewContactClick,
  firstIsPrimary = true,
  defaultRole,
  variant = "plain",
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const color400 = colors.primary[400] || "#724E41";
  const isCard = variant === "card";

  const roleForNewRow = () =>
    firstIsPrimary && rows.length === 0
      ? PRIMARY_CONTACT_ROLE
      : defaultRole || DEFAULT_CONTACT_ROLE;

  const displayRows =
    rows.length === 0
      ? [{ contact_id: 0, role: roleForNewRow(), contact: null }]
      : rows;

  const handleAddRow = () =>
    onChange([
      ...rows,
      { contact_id: 0, role: roleForNewRow(), contact: null },
    ]);

  const handleRowSelect = (index: number, value: IContactFormData | null) => {
    if (rows.length === 0) {
      if (!value) return;
      onChange([
        {
          contact_id: Number(value.id),
          role: roleForNewRow(),
          contact: buildRowContact(value),
        },
      ]);
      return;
    }
    onChange(
      rows.map((row, i) =>
        i === index
          ? {
              ...row,
              contact_id: value ? Number(value.id) : 0,
              contact: buildRowContact(value),
            }
          : row,
      ),
    );
  };

  const handleRoleChange = (index: number, role: string) => {
    let next = rows.map((row, i) => (i === index ? { ...row, role } : row));
    // Only one primary is allowed.
    if (role === PRIMARY_CONTACT_ROLE) {
      next = next.map((row, i) =>
        i !== index && row.role === PRIMARY_CONTACT_ROLE
          ? { ...row, role: DEFAULT_CONTACT_ROLE }
          : row,
      );
    }
    onChange(next);
  };

  const handleRemoveRow = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));

  const renderRow = (row: BookingContactEntry, index: number) => {
    const rowValue: IContactFormData | null = row.contact
      ? ({
          id: row.contact.id,
          first_name: row.contact.first_name ?? "",
          last_name: row.contact.last_name ?? "",
          email: row.contact.email ?? "",
          phone: row.contact.phone ?? "",
        } as IContactFormData)
      : contactOptions.find((c) => c.id === row.contact_id) || null;

    return (
      <Box
        key={index}
        sx={
          isCard
            ? {
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                p: 1.25,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }
            : {
                display: "flex",
                gap: 1,
                mb: 0.5,
                alignItems: "flex-start",
              }
        }
      >
        <Box sx={{ flex: 2 }}>
          <Autocomplete
            fullWidth
            size={isCard ? "small" : undefined}
            options={contactOptions}
            getOptionLabel={(o) =>
              `${o.first_name || ""} ${o.last_name || ""}`.trim()
            }
            value={rowValue}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_e, value) => handleRowSelect(index, value)}
            onInputChange={(_e, value) => setContactSearchTerm(value)}
            loading={contactLoading}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={`Contact ${index + 1}`}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {contactLoading && (
                        <CircularProgress
                          size={isCard ? 16 : 18}
                          sx={{ color: color400 }}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <CustomSelect
            label="Role"
            value={row.role}
            onChange={(e) => handleRoleChange(index, e.target.value as string)}
          >
            {CONTACT_ROLE_OPTIONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>
        {!(rows.length === 0 && index === 0) && (
          <IconButton
            aria-label="remove contact"
            size={isCard ? "small" : undefined}
            onClick={() => handleRemoveRow(index)}
            sx={{ mt: 0.5, color: isCard ? "text.secondary" : color400 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  const addButtonSx = {
    color: color400,
    textTransform: "uppercase" as const,
    fontSize: isCard ? "11.5px" : "12px",
    fontWeight: 700,
    p: 0,
    minWidth: 0,
    ...(isCard ? {} : { mt: 1 }),
    "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
  };
  const addIconSx = {
    fontSize: isCard ? "15px !important" : "16px !important",
  };

  return (
    <Box>
      {isCard ? (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
        >
          {displayRows.map(renderRow)}
        </Box>
      ) : (
        displayRows.map(renderRow)
      )}

      <Typography
        variant="caption"
        sx={{
          color: "#A0AEC0",
          display: "block",
          mb: 0.5,
          fontSize: "11px",
        }}
      >
        Type at least 3 characters to search contacts
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          ...(isCard ? { alignItems: "center" } : {}),
        }}
      >
        <Button 
          startIcon={<Add sx={addIconSx} />}
          onClick={handleAddRow} 
          sx={addButtonSx}
        >
          Add Contact
        </Button>
        {onNewContactClick && (
          <Button
            startIcon={<Add sx={addIconSx} />}
            onClick={onNewContactClick}
            sx={addButtonSx}
          >
            New Contact
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default BookingContactsField;
